import Image from 'next/image';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import { delay, SectionHeading, tone } from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

const R2 = 'https://media.easy-peasy.ai/static/workshop';

// Pre-sized illustrations in R2, served as-is (no Vercel image optimization).
const PEOPLE = [
  {
    title: 'Entrepreneurs',
    text: 'Recommend the tools you already run your business on.',
    image: `${R2}/busy-entrepreneur-singapore-ai-workshop-v3.webp`,
  },
  {
    title: 'Creators and influencers',
    text: 'Show what you make with AI and earn when viewers subscribe.',
    image: `${R2}/influencer-singapore-ai-workshop-v3.webp`,
  },
  {
    title: 'Marketers and agencies',
    text: 'Point clients to the platform you use for their content.',
    image: `${R2}/marketing-agency-singapore-ai-workshop-v3.webp`,
  },
  {
    title: 'Freelancers and consultants',
    text: 'Add a recurring income stream next to your client work.',
    image: `${R2}/freelancer-singapore-ai-workshop-v3.webp`,
  },
];

const REASONS = [
  {
    value: '3.4M+',
    title: 'People already use it',
    text: 'Across 150+ countries and 100+ languages, so your audience may well have heard of it.',
  },
  {
    value: 'All-in-one',
    title: 'Something for everyone',
    text: 'Marky Agent, AI agents, image, video and voice in one subscription. More of your audience finds a reason to pay.',
  },
  {
    value: 'Free plan',
    title: 'Easy to try',
    text: 'People can start free and see the value before they pay for a plan.',
  },
];

export default function AffiliatesAudience() {
  const { ref, state } = useReveal<HTMLUListElement>(0.1);

  return (
    <section className='pb-24 sm:pb-32'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title='Who it’s for.'
          tail='Anyone people ask for recommendations.'
          lead='You don’t need a big following or a paid plan to join. If people trust your advice on tools, this fits.'
        />
      </div>

      {/* Phones scroll the cards sideways; larger screens show a grid. */}
      <ul
        ref={ref}
        data-reveal={state}
        className={clsxm(
          styles.reveal,
          'mt-14 flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pb-4 [scrollbar-width:none] sm:mx-auto sm:grid sm:max-w-7xl sm:grid-cols-2 sm:overflow-visible sm:pb-0 lg:grid-cols-4 lg:px-8 [&::-webkit-scrollbar]:hidden',
        )}
      >
        {PEOPLE.map(({ title, text, image }, index) => (
          <li
            key={title}
            className={clsxm(
              styles.rv,
              'relative aspect-[4/5] w-[78%] flex-none snap-start overflow-hidden rounded-[24px] bg-[#e8e8f0] sm:w-auto dark:bg-white/5',
            )}
            style={delay(index * 70)}
          >
            <Image
              src={image}
              alt=''
              fill
              unoptimized
              className='object-cover'
            />
            <div className='absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0d0c1f]/90 via-[#0d0c1f]/55 to-transparent px-5 pb-5 pt-24'>
              <h3 className='text-lg font-semibold tracking-[-0.015em] text-white'>
                {title}
              </h3>
              <p className='mt-1 text-[14px] leading-6 text-white/80'>{text}</p>
            </div>
          </li>
        ))}
      </ul>

      <div className='mx-auto mt-4 grid max-w-7xl gap-4 px-6 md:grid-cols-3 lg:px-8'>
        {REASONS.map(({ value, title, text }) => (
          <div
            key={title}
            className={clsxm('rounded-[22px] p-7', tone.surface, tone.line)}
          >
            <p
              className={clsxm(
                styles.display,
                'text-[2rem] font-semibold leading-none text-primary-600 dark:text-primary-400',
              )}
            >
              {value}
            </p>
            <h3
              className={clsxm(
                'mt-5 text-lg font-semibold tracking-[-0.01em]',
                tone.ink,
              )}
            >
              {title}
            </h3>
            <p className={clsxm('mt-1.5 text-[15px] leading-7', tone.body)}>
              {text}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}
